import React, { FC, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { AxiosError } from "axios";

import ArticleLimits from "./components/ArticleLimits";
import ArticleLimitModal, {
  IArticleLimitFormData,
} from "./components/ArticleLimitModal";
import { useDialog } from "../../hooks";
import {
  fetchOptionsTrigger,
  selectOptionsIsFetching,
  selectOptionsLimit,
  updateOptionsTrigger,
} from "../../store/account";
import TransferStats from "./components/TransferStats";
import Card from "../../components/Card";
import ManageServicesTable from "./components/ManageServicesTable";
import {
  fetchUsage,
  selectCurrentTransfer,
  selectUsageFetching,
  selectUsageList,
  selectUsageSummary,
} from "../../store/usage";
import {
  editSubscriptionOptions,
  fetchSubscriptionOptions,
  recycleSubscription,
  selectCurrentSubscriptionId,
  selectIsFetchingSubscriptionOptions,
  selectIsRecycleFetching,
  selectSubscriptionAutoRecycle,
} from "../../store/subscriptions";
import Message from "../../components/Table/components/Message";
import ConfirmManualRecycleModal from "./components/ConfirmManualRecycleModal";
import Toaster from "../../services/toaster";
import { selectNextInvoiceData } from "../../store/common/selectors";
import { selectCustomerData } from "../../store/user";
import { getServiceCodeByTenant } from "../../utils/tenant";
import { checkIsETagError } from "../../utils/common";
import { ContentLoaderContainer } from "../../components/Loader/ContentLoader/ContentLoaderContainer";

import "./styles.scss";

export const ManageServices: FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isOpen, openModal, closeModal] = useDialog();
  const [
    isConfirmManualRecycleOpen,
    openConfirmManualRecycleOpen,
    closeConfirmManualRecycleOpen,
  ] = useDialog();
  const limitValue = useSelector(selectOptionsLimit);
  const isFetchingOptions = useSelector(selectOptionsIsFetching);
  const usageList = useSelector(selectUsageList);
  const summary = useSelector(selectUsageSummary);
  const currentSubscriptionId = useSelector(selectCurrentSubscriptionId);
  const nextInvoiceData = useSelector(selectNextInvoiceData);
  const currentTransfer = useSelector(selectCurrentTransfer);
  const isFetching = useSelector(selectUsageFetching);
  const isAutoRecycleActive = useSelector(selectSubscriptionAutoRecycle);
  const isFetchingSubscriptionOptions = useSelector(
    selectIsFetchingSubscriptionOptions,
  );
  const isRecycleFetching = useSelector(selectIsRecycleFetching);
  const customer = useSelector(selectCustomerData);
  const tenantCode = customer?.tenant_code;
  const serviceCode = useMemo(() => {
    return getServiceCodeByTenant(tenantCode);
  }, [tenantCode]);

  useEffect(() => {
    dispatch(fetchOptionsTrigger(serviceCode));
  }, [dispatch, serviceCode]);

  useEffect(() => {
    if (currentSubscriptionId && !usageList.length) {
      dispatch(
        fetchUsage({
          subscriptionId: currentSubscriptionId,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, currentSubscriptionId]);

  useEffect(() => {
    if (currentSubscriptionId) {
      dispatch(
        fetchSubscriptionOptions({
          subscriptionId: currentSubscriptionId,
        }),
      );
    }
  }, [dispatch, currentSubscriptionId]);

  const isQuotaAvalible = useMemo(() => {
    return currentTransfer && currentTransfer?.quota > 0;
  }, [currentTransfer]);

  const onUpdateLimitSuccess = useCallback(() => {
    Toaster.success({ content: t("articleLimitSuccessMessage") });
  }, [t]);

  const onUpdateLimitError = useCallback(
    (error: AxiosError) => {
      if (checkIsETagError(error.response?.data.error_code)) {
        Toaster.error({
          content: t(`error.${error.response?.data.error_code}`),
        });
        return;
      }

      Toaster.error({ content: t("somethingWentWrong") });
    },
    [t],
  );

  const handleUpdateLimit = useCallback(
    (data: IArticleLimitFormData) => {
      closeModal();

      dispatch(
        updateOptionsTrigger({
          limit: Number(data.limit),
          onSuccess: onUpdateLimitSuccess,
          onError: onUpdateLimitError,
          serviceCode,
        }),
      );
    },
    [
      dispatch,
      onUpdateLimitError,
      onUpdateLimitSuccess,
      closeModal,
      serviceCode,
    ],
  );

  const onRecycleSuccess = () => {
    Toaster.success({ content: t("planWasUpgraded") });
    closeConfirmManualRecycleOpen();
  };

  const onRecycleError = () => {
    Toaster.error({ content: t("thereWasAnIssueProcessingYourPayment") });
  };

  const handleConfirmRecycle = () => {
    if (currentSubscriptionId) {
      dispatch(
        recycleSubscription({
          subscriptionId: currentSubscriptionId,
          onSuccess: onRecycleSuccess,
          onError: onRecycleError,
        }),
      );
    }
  };

  const onAutoRecycleSuccess = () => {
    Toaster.success({ content: t("subscriptionUpdatedSuccessfully") });
  };

  const onAutoRecycleError = (error: AxiosError) => {
    if (checkIsETagError(error.response?.data.error_code)) {
      Toaster.error({
        content: t(`error.${error.response?.data.error_code}`),
      });
      return;
    }
    Toaster.error({ content: t("somethingWentWrong") });
  };

  const handleAutoRecycle = (autoRecycle: boolean) => {
    if (currentSubscriptionId) {
      dispatch(
        editSubscriptionOptions({
          autoRecycle,
          subscriptionId: currentSubscriptionId,
          onSuccess: onAutoRecycleSuccess,
          onError: onAutoRecycleError,
        }),
      );

      if (
        autoRecycle &&
        currentTransfer &&
        isQuotaAvalible &&
        currentTransfer.quota <= currentTransfer.amount
      ) {
        dispatch(
          recycleSubscription({
            subscriptionId: currentSubscriptionId,
          }),
        );
      }
    }
  };

  return (
    <section className="ams-manage-services">
      {isFetching ? (
        <ContentLoaderContainer />
      ) : (
        <>
          <h1 className="ams-manage-services__header">
            {t("manageServicesTab")}
          </h1>

          <ArticleLimits
            limitValue={limitValue}
            isLoading={isFetchingOptions}
            onClick={openModal}
          />

          <Card className="ams-manage-services__card">
            <h2 className="mt-0 mb-l">{t("transferStats")}</h2>
            <TransferStats
              isFetchingSubscriptionOptions={isFetchingSubscriptionOptions}
              isAutoRecycleActive={isAutoRecycleActive}
              currentTransfer={currentTransfer}
              nextRotation={nextInvoiceData?.nextInvoicesAt}
              lifetimeTransfer={summary?.bytes}
              onRecycle={openConfirmManualRecycleOpen}
              onAutoRecycle={handleAutoRecycle}
            />
            {isFetching ? (
              <ContentLoaderContainer />
            ) : !usageList.length ? (
              <Message>{t("nothingToDisplay")}</Message>
            ) : (
              <ManageServicesTable data={usageList} summary={summary} />
            )}
          </Card>

          {isOpen && (
            <ArticleLimitModal
              isOpen={isOpen}
              limitValue={limitValue}
              onDismiss={closeModal}
              onUpdate={handleUpdateLimit}
            />
          )}

          {isConfirmManualRecycleOpen && nextInvoiceData && (
            <ConfirmManualRecycleModal
              nextInvoice={nextInvoiceData}
              isOpen={isConfirmManualRecycleOpen}
              isLoading={isRecycleFetching}
              onConfirm={handleConfirmRecycle}
              onDismiss={closeConfirmManualRecycleOpen}
            />
          )}
        </>
      )}
    </section>
  );
};
